import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { getDayTypeDotColors } from './cUtils';
import { centered } from '@ui/style/style';
const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];
function getMonthMatrix(year, month) {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const matrix = [];
    let week = [];
    let day = new Date(firstDay);
    for (let i = 0; i < firstDay.getDay(); i++)
        week.push(null);
    while (day <= lastDay) {
        week.push(new Date(day));
        if (week.length === 7) {
            matrix.push(week);
            week = [];
        }
        day.setDate(day.getDate() + 1);
    }
    if (week.length) {
        while (week.length < 7)
            week.push(null);
        matrix.push(week);
    }
    return matrix;
}
export const YearView = ({ year, selectedDate, onDateClick, isSponsorshipDetailsCalendarTab = false, items = [], handleOpenModal }) => {
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    let columns = 4;
    if (isMd)
        columns = 3;
    if (isSm)
        columns = 2;
    if (isXs)
        columns = 1;
    return (_jsx(Box, { display: 'grid', sx: {
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: 4,
            alignItems: 'flex-start',
            bgcolor: theme.palette.background.paper,
            borderRadius: theme.spacing(3),
            padding: 2,
            maxHeight: isXs && isSponsorshipDetailsCalendarTab
                ? 'calc(100vh - 350px)'
                : isSponsorshipDetailsCalendarTab
                    ? 'calc(100vh - 300px)'
                    : 'calc(100vh - 370px)',
            overflowY: 'auto'
        }, children: months.map((monthName, monthIdx) => (_jsxs(Box, { minWidth: 220, mb: 0, children: [_jsx(Typography, { variant: 'caption', fontWeight: 400, mb: 1, children: monthName }), _jsxs(Box, { children: [_jsx(Box, { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', children: [...'SMTWTFS'].map((d, i) => (_jsx(Typography, { variant: 'caption', color: 'text.primary', children: d }, i))) }), getMonthMatrix(year, monthIdx).map((week, i) => (_jsx(Box, { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', children: week.map((date, j) => {
                                const isCurrentMonth = date && date.getMonth() === monthIdx;
                                const isSelected = date &&
                                    date.getDate() === selectedDate.getDate() &&
                                    date.getMonth() === selectedDate.getMonth() &&
                                    date.getFullYear() === selectedDate.getFullYear();
                                // Filter events for this date
                                const dayItems = date
                                    ? items.filter((item) => {
                                        // Normalize dates to midnight for comparison
                                        const dateNormalized = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                                        if (['event'].includes(item.type)) {
                                            const itemStartDate = new Date(item.start.getFullYear(), item.start.getMonth(), item.start.getDate());
                                            const itemEndDate = new Date(item.end.getFullYear(), item.end.getMonth(), item.end.getDate());
                                            return dateNormalized >= itemStartDate && dateNormalized <= itemEndDate;
                                        }
                                        return item.start.toDateString() === date.toDateString();
                                    })
                                    : [];
                                const hasEvents = dayItems.length > 0;
                                return (_jsx(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '100%',
                                        height: '100%'
                                    }, children: _jsxs(Box, { sx: {
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 0.5,
                                            cursor: isCurrentMonth && date ? 'pointer' : 'default',
                                            padding: 0.5
                                        }, onClick: (e) => {
                                            e.stopPropagation();
                                            if (isCurrentMonth && date) {
                                                if (hasEvents && handleOpenModal && dayItems.length > 0) {
                                                    // Set window globals for dialog
                                                    window.calendarDialogItems = dayItems;
                                                    window.calendarDialogSelectedDate = date;
                                                    window.calendarDialogAllItems = items;
                                                    // Open dialog with first event
                                                    handleOpenModal({ ...dayItems[0], start: date });
                                                }
                                                else {
                                                    // Navigate to date (original behavior - fallback if no events or no modal handler)
                                                    onDateClick?.(date);
                                                }
                                            }
                                        }, children: [_jsx(Box, { sx: {
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: '50%',
                                                    textAlign: 'center',
                                                    lineHeight: '24px',
                                                    fontSize: '12px',
                                                    fontWeight: 400,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: isSelected ? theme.palette.primary.darker : 'transparent',
                                                    color: isSelected ? theme.palette.common.white : 'inherit',
                                                    opacity: isCurrentMonth ? 1 : 0.3,
                                                    transition: 'background 0.15s',
                                                    '&:hover': isCurrentMonth && date
                                                        ? {
                                                            bgcolor: isSelected
                                                                ? theme.palette.primary.dark
                                                                : theme.palette.action.hover
                                                        }
                                                        : undefined,
                                                    '&:active': isCurrentMonth && date
                                                        ? {
                                                            bgcolor: theme.palette.primary.light
                                                        }
                                                        : undefined
                                                }, children: date ? date.getDate() : '' }), hasEvents && (_jsx(Box, { sx: {
                                                    ...centered,
                                                    flexDirection: 'row',
                                                    gap: 0.25
                                                }, children: getDayTypeDotColors(dayItems, false, theme).map((color, idx) => (_jsx(Box, { sx: {
                                                        width: 4,
                                                        height: 4,
                                                        borderRadius: '50%',
                                                        backgroundColor: color
                                                    } }, idx))) }))] }) }, j));
                            }) }, i)))] })] }, monthName))) }));
};
export default YearView;
